import Cookies from 'js-cookie';
import { HttpService } from 'services/client/types';
import CalculatorService from 'services/Django/CalculatorService';
import CryptoService from 'src/services/Django/CryptoService';
import UserService from 'src/services/Django/UserService';
import DataCenterService from 'src/services/Django/DataCenterService';
import PowerGridService from 'src/services/Django/PowerGridService';
import BalanceService from 'src/services/Django/BalanceService';
import BuilderService from 'services/Django/BuilderService';
import { LocalStorageKeys } from 'appConstants/LocalStorageKeys';
import CartService from 'services/Django/CartService';
import MarketPlaceService from 'services/Django/MarketplaceService';
import GemsService from 'services/Django/GemsService';
import LiquidationService from 'services/Django/LiquidationService';
import AchievementsService from 'services/Django/AchievementsService';
import MyNftsService from 'services/Django/MyNftsService';
import SupportService from 'services/Django/SupportService';
import NewsService from 'services/Django/NewsService';
import NotificationService from 'services/Django/NotificationService';
import VaultService from 'services/Django/VaultService';
import { AppRoutes } from 'router';
import { createHttpInstance, HttpErrorResponse, HttpRequestConfig, HttpResponse } from 'services/httpClient';

const API_URL = import.meta.env.VITE_DEV_DJANGO;

type RefreshType = { access: string; refresh: string };

// Одно общее обещание на время обновления пары токенов, чтобы параллельные
// запросы, получившие 401, не запускали refresh одновременно.
let isRefreshingFunc: undefined | Promise<HttpResponse<RefreshType> | HttpErrorResponse<RefreshType>>;

export const instance = createHttpInstance({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'en',
  },
});

const refreshingFunc = (token: string) =>
  instance.post<RefreshType>('/v2/users/token/refresh/', {
    refresh: JSON.parse(token),
  });

instance.interceptors.request.use(async (config: HttpRequestConfig) => {
  const token = Cookies.get(LocalStorageKeys.TOKEN_KEY);
  const vaultToken = Cookies.get(LocalStorageKeys.VAULT_TOKEN_KEY);
  const url = config.url ?? '';
  if (token && !url.includes('status') && !url.includes('refresh')) {
    const headers = { ...(config.headers ?? {}) };
    headers.Authorization = `Bearer ${JSON.parse(token)}`;
    if (vaultToken) {
      headers['X-Vault-Access-Token'] = `Bearer ${JSON.parse(vaultToken)}`;
    }
    return { ...config, headers };
  }
  return config;
});

const clearTokensAndRedirect = () => {
  Cookies.remove(LocalStorageKeys.TOKEN_KEY);
  Cookies.remove(LocalStorageKeys.REFRESH_KEY);
  Cookies.remove(LocalStorageKeys.VAULT_TOKEN_KEY);
  Cookies.remove(LocalStorageKeys.VAULT_REFRESH_KEY);
  window.location.href = AppRoutes.SIGN_IN;
};

instance.interceptors.response.use(
  // onFulfilled — пробрасываем как есть
  async (response) => response,
  // onRejected — обработка 401 с refresh-токеном
  async (error) => {
    const originalConfig = error.config;
    const isUnauthorized = error.response?.status === 401;
    const isRefreshCall = originalConfig?.url?.includes('refresh');

    if (!originalConfig || !isUnauthorized || isRefreshCall) {
      return error;
    }

    try {
      const refreshToken = Cookies.get(LocalStorageKeys.REFRESH_KEY) || '';
      if (!refreshToken) {
        clearTokensAndRedirect();
        return error;
      }
      if (!isRefreshingFunc) isRefreshingFunc = refreshingFunc(refreshToken);
      const refreshResult = await isRefreshingFunc;
      if (refreshResult.isError || !refreshResult.data) {
        clearTokensAndRedirect();
        return error;
      }
      const { access, refresh } = refreshResult.data;
      Cookies.set(LocalStorageKeys.TOKEN_KEY, JSON.stringify(access), { expires: 1 });
      Cookies.set(LocalStorageKeys.REFRESH_KEY, JSON.stringify(refresh), { expires: 3 });

      // Повтор оригинального запроса с новым токеном
      const retryHeaders = { ...(originalConfig.headers ?? {}) };
      retryHeaders.Authorization = `Bearer ${access}`;
      return await instance.request({ ...originalConfig, headers: retryHeaders });
    } catch {
      clearTokensAndRedirect();
      return error;
    } finally {
      isRefreshingFunc = undefined;
    }
  },
);

export const httpService: HttpService = {
  CalculatorService,
  CryptoService,
  UserService,
  DataCenterService,
  PowerGridService,
  CartService,
  BalanceService,
  MarketPlaceService,
  BuilderService,
  GemsService,
  LiquidationService,
  AchievementsService,
  MyNftsService,
  NewsService,
  SupportService,
  NotificationService,
  VaultService,
};
